import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { FullBox, useStateInRef } from 'seagull';
import { GoogleMapsProvider, loadGoogleMaps } from '~/components/AddressAutocomplete';
import { AgreementTriageModalOpener } from '~/components/AgreementTriageModal/AgreementTriageModalOpener';
import ErrorBoundaryPage from '~/components/ErrorBoundaryPage/ErrorBoundaryPage';
import FullPageInvoiceOpener from '~/components/FullPageInvoice/FullPageInvoiceOpener';
import InAppNotificationBinder from '~/components/InAppNotificationsBinder';
import InAppNotificationDrawer from '~/components/InAppNotificationsDrawer/InAppNotificationsDrawer';
import { LoginErrorModal } from '~/components/LoginErrorModal';
import { MobileDeviceEnhancer } from '~/components/MobileDeviceEnhancer/MobileDeviceEnhancer';
import HomeSidebar, { getSidebarRoutes, sidebarTextsPath } from '~/components/NavigationAndStructure/HomeSidebar/HomeSidebar';
import { TopBar } from '~/components/NavigationAndStructure/TopBar/';
import { getUserMenuRoutes, userMenuRouteTextsPath } from '~/components/NavigationAndStructure/UserMenu/UserMenu';
import RestfulWrapper from '~/components/RestfulWrapper/RestfulWrapper';
import { RouteWrapper } from '~/components/RouteWrapper';
import { SecureLoginModal } from '~/components/SecureLoginModal/SecureLoginModal';
import { useQboInVob } from '~/components/VendorOnboardingModal/useQboInVob';
import VendorOnboardingModal from '~/components/VendorOnboardingModal/VendorOnboardingModal';
import { defaultAuthorizedRoute, routes, UtmParams } from '~/constants';
import { useIoContext, useOverlayContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { useCreateVendorAccount } from '~/hooks/useCreateVendorAccount/useCreateVendorAccount';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useHomePageRoutesByUrl } from '~/hooks/useHomePageRoutesByUrl';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useOnMount } from '~/hooks/useOnMount';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { importContactsRoute } from '~/pages/ContactsDashboard';
import { importServicesRoute } from '~/pages/ServicesLibrary/ServicesPage/ServicesPage';
import { selectors } from '~/store/state-selectors';
import { TasksDrawerRoot } from '~/tasks-drawer/TasksDrawerRoot';
import { VobQboState } from '~/types/api.model';
import { AnchorForBusinessBanner } from './AnchorForBusinessBanner';
import { MissingColumnBankAccountBanner } from './Banners';
import styles from './HomePage.module.scss';
import HomePageProvider, { useHomePage } from './HomePageProvider';
const appRoutes = [
    {
        id: 'minisite',
        text: '',
        url: routes.minisite(undefined),
        shouldShow: true
    }
];
const SECURE_LOGIN_SET_FLAG = 'secure-login-set-success';
function CreateVendorAccountForClientWithReferral() {
    const getUtms = useUtmsParams();
    const createVendorAccount = useCreateVendorAccount();
    const { getState } = useStoreStatic();
    useOnMount(()=>{
        var _getState_userInfo;
        const utms = getUtms();
        const isCustomer = (_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : _getState_userInfo.isCustomer();
        if (utms[UtmParams.Referral] && isCustomer) {
            createVendorAccount();
        }
    });
    return null;
}
export function ActualHomePage(props) {
    const { t: sidebarTexts } = useLocaleKeys(sidebarTextsPath);
    const { showModal, showSuccessToast } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const history = useHistory();
    const userInfo = useStore((state)=>state.userInfo);
    const { logBI } = useStoreActions();
    const { hidden } = useHomePage();
    const { localStorage, browser: { locationReload } } = useIoContext();
    const isVendor = useStore(selectors.user.isVendor);
    const featureFlags = useFeatureFlags();
    const { CONNECTED_APPS_PAGE, SP_ONBOARDING_PHASE_1 = false, VOB_EARLY_BUSINESS_CREATION } = featureFlags;
    const { t: userMenuTexts } = useLocaleKeys(userMenuRouteTextsPath);
    const pageLinks = useStore(selectors.filterAvailableRoutes(getSidebarRoutes({
        texts: sidebarTexts,
        CONNECTED_APPS_PAGE
    }).concat(getUserMenuRoutes(userMenuTexts)).concat(importContactsRoute).concat(importServicesRoute).concat(appRoutes)));
    const isMobileDevice = useIsMobileForHomePage();
    const componentMountMap = useHomePageRoutesByUrl();
    const firstName = userInfo === null || userInfo === void 0 ? void 0 : userInfo.firstName();
    const lastName = userInfo === null || userInfo === void 0 ? void 0 : userInfo.lastName();
    const hasName = !!firstName && !!lastName;
    const isClient = userInfo === null || userInfo === void 0 ? void 0 : userInfo.isCustomer();
    const hasBusiness = !!(userInfo === null || userInfo === void 0 ? void 0 : userInfo.hasBusiness());
    const claims = useStore((state)=>state.claims);
    const oldVobDone = hasName && hasBusiness;
    const isReturningToIncompleteVendor = VOB_EARLY_BUSINESS_CREATION ? (claims === null || claims === void 0 ? void 0 : claims.requireVendorDetailsCompletion) && (claims === null || claims === void 0 ? void 0 : claims.businessId) === (userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId()) : false;
    const vobDone = VOB_EARLY_BUSINESS_CREATION ? oldVobDone && !isReturningToIncompleteVendor : oldVobDone;
    const isAnchorAdmin = useStore(selectors.user.isAnchorAdmin);
    const { getState: getVobQboState } = useQboInVob();
    const { getState } = useStoreStatic();
    const { showQboStep } = useQboInVob();
    const getEffectData = useStateInRef({
        getVobQboState
    });
    const isSetIdentityFlow = useStore(selectors.isSetIdentityFlow);
    const location = useLocation();
    const isOnLoginErrorRoute = location.pathname.includes(routes.loginError);
    const [secureLoginModalOpen, setSecureLoginModalOpen] = useState(false);
    const vobModalOpenRef = useRef(false);
    useOnMount(()=>{
        if (localStorage.getItem(SECURE_LOGIN_SET_FLAG) === 'true') {
            localStorage.removeItem(SECURE_LOGIN_SET_FLAG);
            showSuccessToast(commonTexts.toasts.secureLoginSetSuccessfully(), 'secure-login-set-success-toast');
        }
    });
    useEffect(()=>{
        // isSetIdentityFlow is true when the user has a business without an identity.
        // not showing SecureLoginModal if user is on login error route or if user already completed the flow
        if (isSetIdentityFlow && !isOnLoginErrorRoute && !secureLoginModalOpen) {
            logBI(18900);
            setSecureLoginModalOpen(true);
            showModal({
                closeOnClickOutside: false,
                closeOnEscapeKey: false,
                biName: 'secure_login',
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(SecureLoginModal, {
                        dismissModal: dismissModal,
                        isDismissDisabled: true
                    });
                }
            }).then((completed)=>{
                setSecureLoginModalOpen(false);
                if (completed) {
                    locationReload();
                }
            });
        }
    }, [
        isSetIdentityFlow,
        isOnLoginErrorRoute,
        secureLoginModalOpen
    ]);
    useEffect(()=>{
        const openVOB = (vobQboState)=>{
            vobModalOpenRef.current = true;
            return showModal({
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(GoogleMapsProvider, {
                        loadGoogleMaps: loadGoogleMaps,
                        children: /*#__PURE__*/ _jsx(VendorOnboardingModal, {
                            dismissModal: dismissModal,
                            vobQboState: vobQboState,
                            showQboStep: !!showQboStep
                        })
                    });
                },
                closeOnClickOutside: false,
                shouldBlurBackground: true,
                biName: 'vendor_onboarding_modal'
            }).then((finishedVOB)=>{
                vobModalOpenRef.current = false;
                return finishedVOB;
            });
        };
        if (hidden || vobModalOpenRef.current || SP_ONBOARDING_PHASE_1) return;
        const { getVobQboState } = getEffectData();
        getVobQboState().then((vobQboState)=>{
            const isLinkedToQbo = selectors.integration.isLinkedToQbo(getState());
            const startedQboFlow = vobQboState === VobQboState.Started;
            const pendingQboSetup = vobQboState === VobQboState.ConnectionTriggered && isLinkedToQbo;
            if (!isClient && (!vobDone || (startedQboFlow || pendingQboSetup) && showQboStep)) {
                history.push({
                    pathname: routes.vendorDashboard,
                    search: location.search
                });
                if (VOB_EARLY_BUSINESS_CREATION) {
                    const reopenUntilFinished = ()=>openVOB(vobQboState).then(async (finishedVOB)=>{
                            if (!finishedVOB) return reopenUntilFinished();
                        });
                    reopenUntilFinished();
                } else {
                    openVOB(vobQboState).then(async (finishedVOB)=>{
                        if (!finishedVOB) openVOB(vobQboState);
                    });
                }
            }
        });
    }, [
        hidden,
        hasBusiness,
        isClient,
        showQboStep,
        vobDone,
        SP_ONBOARDING_PHASE_1,
        VOB_EARLY_BUSINESS_CREATION
    ]);
    function getAllNavigation(items) {
        return items.flatMap((item)=>'url' in item ? item : 'items' in item ? getAllNavigation(item.items) : []);
    }
    const shouldHideForNoBusiness = claims != null && !claims.businessId;
    if (hidden) return null;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: !shouldHideForNoBusiness && /*#__PURE__*/ _jsxs(FullBox, {
            flexDirection: "row",
            className: cn(styles.mainContainer, {
                [styles.anchorAdmin]: isAnchorAdmin
            }),
            testId: "home-screen",
            children: [
                /*#__PURE__*/ _jsx(CreateVendorAccountForClientWithReferral, {}),
                /*#__PURE__*/ _jsx(FullPageInvoiceOpener, {}),
                /*#__PURE__*/ _jsx(AgreementTriageModalOpener, {}),
                /*#__PURE__*/ _jsx(InAppNotificationDrawer, {}),
                /*#__PURE__*/ _jsx(TasksDrawerRoot, {}),
                !isMobileDevice && /*#__PURE__*/ _jsx(HomeSidebar, {}),
                /*#__PURE__*/ _jsx(FullBox, {
                    flexDirection: "column",
                    className: styles.mainContent,
                    children: /*#__PURE__*/ _jsxs(FullBox, {
                        flexDirection: "column",
                        children: [
                            /*#__PURE__*/ _jsx(TopBar, {}),
                            /*#__PURE__*/ _jsxs(MobileDeviceEnhancer, {
                                enabled: isMobileDevice,
                                maxHeight: false,
                                children: [
                                    /*#__PURE__*/ _jsx(MissingColumnBankAccountBanner, {}),
                                    /*#__PURE__*/ _jsx(AnchorForBusinessBanner, {})
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: styles.pageContent,
                                children: /*#__PURE__*/ _jsx(ErrorBoundaryPage, {
                                    bounded: true,
                                    children: /*#__PURE__*/ _jsxs(Switch, {
                                        children: [
                                            props.children,
                                            getAllNavigation(pageLinks).map((param)=>{
                                                let { url, exact } = param;
                                                return /*#__PURE__*/ _jsx(Route, {
                                                    path: url,
                                                    exact: exact,
                                                    render: (props)=>/*#__PURE__*/ _jsx(RouteWrapper, _object_spread({
                                                            config: componentMountMap[url]
                                                        }, props))
                                                }, url);
                                            }),
                                            /*#__PURE__*/ _jsx(Redirect, {
                                                to: !isClient && !vobDone ? SP_ONBOARDING_PHASE_1 ? routes.vendorOnboarding : routes.vendorDashboard : defaultAuthorizedRoute(isVendor)
                                            })
                                        ]
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Route, {
                                path: "*".concat(routes.loginError),
                                render: ()=>/*#__PURE__*/ _jsx(LoginErrorModal, {})
                            })
                        ]
                    })
                })
            ]
        })
    });
}
export default function HomePage(props) {
    const initialData = useStore((s)=>s.personaKYBStatus.data);
    const { fetchPersonaKYBStatus } = useStoreActions();
    return /*#__PURE__*/ _jsx(RestfulWrapper, {
        initialData: initialData,
        fetch: fetchPersonaKYBStatus,
        renderChild: ()=>/*#__PURE__*/ _jsxs(HomePageProvider, {
                children: [
                    /*#__PURE__*/ _jsx(InAppNotificationBinder, {}),
                    /*#__PURE__*/ _jsx(ActualHomePage, _object_spread({}, props))
                ]
            })
    });
}
